<template>
  <Dialog :open="showModal" @update:open="onDialogUpdate">
    <DialogTrigger type="button" class="btn btn-primary">
      {{ $t('Add new exhibitor') }}
    </DialogTrigger>
    <DialogScrollContent>
      <form @submit.prevent="submit">
        <DialogHeader>
          <DialogTitle>
            {{ $t('Associate an exhibitor to this tour') }}
          </DialogTitle>
        </DialogHeader>

        <div v-if="selectedExponent === null">
          <div class="form-group">
            <label for="search-exponent-input">{{ $t('Search for an exhibitor') }}</label>
            <input type="text" class="form-control mt-2" :placeholder="$t('Exhibitor or brand name')"
                   ref="searchInput"
                   id="search-exponent-input" v-model="searchQuery">
          </div>
          <div v-if="searchingExponent">
            {{ $t('Searching…') }}
          </div>
          <div class="list-group">
            <a href v-for="exponent in findedExponents" class="list-group-item list-group-item-action"
               v-bind:class="{'disabled': isAlreadySelected(exponent.id)}"
               v-bind:key="exponent.id" v-on:click.prevent="selectExponent(exponent)" >
              <div class="d-flex w-100 justify-content-between">
                <div>
                  <strong>{{ exponent.name }}</strong><br>
                  <small class="text-muted">{{ brandsList(exponent) }}</small>
                </div>
                <Badge :variant="visibilityVariant(exponent.visibility_name)" class="tw:shrink-0 tw:self-center">
                  {{ $t(exponent.visibility_name) }}
                </Badge>
              </div>
            </a>
          </div>
        </div>
        <div v-else>
          <div class="list-group mb-3">
            <div class="list-group-item">
              <div class="d-flex w-100 justify-content-between align-items-center">
                <div>
                  <strong>{{ selectedExponent.name }}</strong><br>
                  <small class="text-muted">{{ brandsList(selectedExponent) }}</small>
                </div>
                <Badge :variant="visibilityVariant(selectedExponent.visibility_name)" class="tw:shrink-0 tw:self-center">
                  {{ $t(selectedExponent.visibility_name) }}
                </Badge>
              </div>
            </div>
          </div>

          <p class="tw:text-md tw:leading-none tw:font-semibold tw:mt-6 tw:mb-2">
            {{ $t("Associated members to notify") }}
          </p>

          <MemberNotificationList
            :permissions="selectedExponent.permissions"
            @update:selected-ids="selectedUserIds = $event"
          />
        </div>

        <div v-if="Object.keys(errors).length" class="tw:mt-3">
          <p v-for="(messages, field) in errors" :key="field" class="tw:text-sm tw:text-red-600">
            {{ Array.isArray(messages) ? messages[0] : messages }}
          </p>
        </div>

        <DialogFooter class="mt-3">
          <Button type="button" variant="secondary" @click="discardModal">
            {{ $t('Cancel') }}
          </Button>
          <Button type="submit" variant="default" :disabled="selectedExponent === null || submitting">
            {{ $t('Finish the configuration') }}
          </Button>
        </DialogFooter>
      </form>
    </DialogScrollContent>
  </Dialog>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core'
import { Dialog, DialogScrollContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/shadcn/components/ui/button/index.js";
import Badge from "@/components/ui/badge/Badge.vue";
import MemberNotificationList from "@/components/Tour/MemberNotificationList.vue";
import TourExponent from "@/models/TourExponent.js";

const props = defineProps({
  tourId: {
    type: Number,
    required: true,
  },
  searchExponentUrl: {
    type: String,
  },
  exponentsAlreadySelected: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['created'])
const showModal = ref(false)
const searchQuery = ref('');
const searchingExponent = ref(false);
const selectedExponent = ref(null);
const findedExponents = ref([]);
const selectedUserIds = ref([]);
const submitting = ref(false);
const errors = ref({});

const visibilityVariant = (visibility) => {
  const map = { public: 'green', organizer: 'blue', private: 'yellow' };
  return map[visibility] ?? 'zinc';
};

watch(
  () => searchQuery.value,
  (newQuery) => {
    if (newQuery === '') {
      findedExponents.value = [];
    } else {
      searchExponent();
    }
  }
)

const searchExponent = useDebounceFn(
  async () => {
    searchingExponent.value = true
    axios.get(props.searchExponentUrl, {
      params: {
        q: searchQuery.value
      }
    }).then((response) => {
      searchingExponent.value = false
      findedExponents.value = response.data.data
    }).catch((error) => {
      console.error(error)
    }).finally(() => {
      searchingExponent.value = false
    })
  },
  500
)

const selectExponent = (exponent) => {
  if( ! isAlreadySelected(exponent.id) ) {
    selectedExponent.value = exponent;
  }
}

const isAlreadySelected = (id) => {
  return props.exponentsAlreadySelected.includes(id)
}

const brandsList = (exponent) => {
  if (exponent.brands.length > 0) {
    return exponent.brands.map(brand => brand.name).join('/ ')
  }
  return null;
}

const submit = async () => {
  if (submitting.value || selectedExponent.value === null) {
    return;
  }

  submitting.value = true;
  errors.value = {};

  try {
    const created = await new TourExponent({
      tour_id: props.tourId,
      exponent: selectedExponent.value.id,
      user_ids: selectedUserIds.value,
    }).save();

    // Hand the new row to the parent list so it refreshes and updates the greyed-out set.
    emit('created', created);
    discardModal();
  } catch (error) {
    // Validation errors are returned under `message` by the app's exception handler.
    if (error.response?.status === 422) {
      errors.value = error.response.data.message ?? {};
    } else {
      console.error(error);
    }
  } finally {
    submitting.value = false;
  }
}

const discardModal = () => {
  showModal.value = false;
  searchQuery.value = '';
  selectedExponent.value = null;
  selectedUserIds.value = [];
  errors.value = {};
}

const searchInput = ref(null)
const onDialogUpdate = (isOpen) => {
  if (isOpen) {
    showModal.value = true;
    searchInput.value.focus()
  } else {
    discardModal();
  }
}
</script>

<style scoped>

</style>
